import {
    createMiddleware,
    createServerOnlyFn,
    createStart,
} from "@tanstack/react-start";
import {
    createTelemetryRequestHandler,
    recordTelemetryServerFunction,
    setClickHouseWaitUntil,
} from "@wags/telemetry/adapters/tanstack-start.ts";
import { redactContactTokens } from "../contact-token-scrub.mjs";
import {
    deploySkewFunctionMiddleware,
    missingServerFnRequestMiddleware,
} from "./lib/core/deploy-skew-recovery.ts";
import { redactContactTokenPaths } from "./lib/core/short-links/short-link-attribution.ts";
import { getTelemetry } from "./lib/core/telemetry.server.ts";
import { initServerSentry } from "../instrument.server.mjs";

if (import.meta.env.SSR) {
    setClickHouseWaitUntil((await import("@vercel/functions")).waitUntil);
}

function isAriVerificationRequest(request: Request) {
    try {
        return (
            decodeURIComponent(new URL(request.url).pathname)
                .replace(/\/+$/u, "")
                .toLowerCase()
                .match(/^\/ari\/verify(?:\/[a-z0-9_-]{16})?$/u) !== null
        );
    } catch {
        return false;
    }
}

function isPrivatePayloadRequest(request: Request) {
    try {
        const path = decodeURIComponent(new URL(request.url).pathname)
            .replace(/\/+$/u, "")
            .toLowerCase();
        return path === "/api/mcp" || path === "/api/webhooks/meta";
    } catch {
        return false;
    }
}

export const capturePayloads = (request: Request) =>
    isAriVerificationRequest(request) || isPrivatePayloadRequest(request)
        ? ("never" as const)
        : /^\/api\/(webhooks\/|internal\/sendblue\/)/u.test(
                new URL(request.url).pathname,
            )
          ? ("always" as const)
          : ("on-error" as const);

const telemetryFunctionMiddleware = createMiddleware({
    type: "function",
}).server(async ({ serverFnMeta, next }) => {
    await recordTelemetryServerFunction(serverFnMeta);
    return await next();
});

const telemetryRequestMiddleware = createMiddleware({ type: "request" }).server(
    async (input) => {
        if (isAriVerificationRequest(input.request)) return await input.next();
        return await createTelemetryRequestHandler(await getTelemetry(), {
            capture: capturePayloads,
            redactPath: redactContactTokenPaths,
            redactText: redactContactTokens,
        })(input);
    },
);

const sentryGlobals = import.meta.env.SSR
    ? await import("@sentry/tanstackstart-react")
    : null;

const initSentry = createServerOnlyFn(() =>
    initServerSentry({
        environment: import.meta.env.VITE_PUBLIC_SENTRY_ENVIRONMENT,
        release: import.meta.env.VITE_PUBLIC_SENTRY_RELEASE,
    }),
);

if (import.meta.env.SSR) initSentry();

function requestMiddleware() {
    if (!sentryGlobals) {
        return [telemetryRequestMiddleware, missingServerFnRequestMiddleware];
    }
    return [
        ...sentryGlobals.wrapMiddlewaresWithSentry({
            telemetryRequestMiddleware,
        }),
        sentryGlobals.sentryGlobalRequestMiddleware,
        missingServerFnRequestMiddleware,
    ];
}

function functionMiddleware() {
    if (!sentryGlobals) {
        return [deploySkewFunctionMiddleware, telemetryFunctionMiddleware];
    }
    return [
        deploySkewFunctionMiddleware,
        telemetryFunctionMiddleware,
        sentryGlobals.sentryGlobalFunctionMiddleware,
    ];
}

export const startInstance = createStart(() => {
    return {
        requestMiddleware: requestMiddleware(),
        functionMiddleware: functionMiddleware(),
    };
});
